import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { OnboardingShell } from '@/onboarding/OnboardingShell'
import { useChannel } from '@/context/ChannelContext'
import { usePinnedChannelUUID } from '@/onboarding/usePinnedChannelUUID'
import { bookmarkStep, getOnboardingState } from '@/api/onboarding'
import { startTrialCheckout, type AbVariant } from '@/api/payment'
import { activateChoosing, getShowcase, ShowcaseApiError } from '@/api/showcase'
import { track, trackError } from '@/lib/analytics'

// ⚠️ TESTING: вернуть на 'full' (4 900 ₽ × 30 дней) для прода.
// 'trial' = 1 ₽ × 7 дней — для smoke-тестов.
const PAYMENT_VARIANT = 'full' as AbVariant
const PRICE_LABEL = PAYMENT_VARIANT === 'trial' ? '1 ₽' : '4 900 ₽'
const PRICE_PERIOD = PAYMENT_VARIANT === 'trial' ? '7 дней' : '1 месяц'
const PRICE_NUM = PAYMENT_VARIANT === 'trial' ? '1' : '4 900'
const PRICE_UNIT = PAYMENT_VARIANT === 'trial' ? '₽ / 7 дней' : '₽ / месяц'
// Якорь цены «в день»: 4900 / 30 ≈ 163 ₽. Для триала смысла нет — показываем 1.
const PRICE_PER_DAY = PAYMENT_VARIANT === 'trial' ? 1 : Math.round(4900 / 30)

export function TariffPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { channels } = useChannel()
  const channelUUID = usePinnedChannelUUID()
  const channel = channels.find((c) => c.uuid === channelUUID)
  const [email, setEmail] = useState('')
  const [emailError, setEmailError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [pollingPaid, setPollingPaid] = useState(false)
  const [acceptedTerms, setAcceptedTerms] = useState(false)
  const [acceptedMarketing, setAcceptedMarketing] = useState(false)
  // Для скролла + подсветки legal-блока когда юзер жмёт «Оплатить» без согласия.
  const legalRef = useRef<HTMLDivElement>(null)
  const emailRef = useRef<HTMLDivElement>(null)
  const [legalFlash, setLegalFlash] = useState(false)
  const [emailFlash, setEmailFlash] = useState(false)

  // Если юзер вернулся после успешной оплаты (ЮКасса редиректнула обратно) —
  // или payment-webhook уже сработал — поллим state.hasTariff и навигируем дальше.
  useEffect(() => {
    if (!channelUUID) return
    let cancelled = false
    let timer: number | null = null
    const poll = async () => {
      if (cancelled) return
      try {
        const s = await getOnboardingState()
        if (cancelled) return
        if (s.hasTariff) {
          setPollingPaid(false)
          // Showcase-ветка: ShowcaseRulesPage сохранил правило с backfillRequested=false
          // (потому что тарифа на тот момент не было). Сейчас оплата прошла — повторяем
          // activateChoosing с backfillRequested=true чтобы backend запустил backfill.
          if (s.branchPath === 'showcase' && channelUUID) {
            try {
              const showcase = await getShowcase(channelUUID)
              const whatToPublish = showcase.settings?.whatToPublish
              if (whatToPublish && whatToPublish !== 'manual') {
                await activateChoosing(channelUUID, {
                  whatToPublish,
                  backfillRequested: true,
                })
              }
            } catch (e) {
              // 409 (state не editable) — игнорируем, backfill уже стартовал
              if (!(e instanceof ShowcaseApiError && e.status === 409)) {
                trackError(e, 'showcase-post-payment-choosing')
              }
            }
          }
          // Всегда ведём на /final?payment=success — там юзер увидит экран успеха
          // оплаты с чеком, дальше сам жмёт «Дальше — креативы» и идёт в editor.
          // Это даёт ровно один показ payment-success в правильном месте флоу.
          bookmarkStep('final')
          navigate('/onboarding/final?payment=success', { replace: true })
          return
        }
      } catch {}
      if (!cancelled && pollingPaid) {
        timer = window.setTimeout(poll, 3000)
      }
    }
    if (pollingPaid) poll()
    return () => {
      cancelled = true
      if (timer !== null) window.clearTimeout(timer)
    }
  }, [channelUUID, navigate, pollingPaid])

  const [alreadyPaid, setAlreadyPaid] = useState(false)
  const [knownBranch, setKnownBranch] = useState<'ads' | 'quick' | 'analytics' | 'showcase' | null>(null)

  // На маунт — проверяем, оплачено ли уже (для баннера, не авто-skip).
  // Берём 2 источника:
  //  - state.hasTariff — текущая onboarding-сессия оплачена
  //  - channel.paidUntilEpoch > now — у канала исторически активная оплата
  useEffect(() => {
    if (!channelUUID) return
    let cancelled = false
    getOnboardingState()
      .then((s) => {
        if (cancelled) return
        const channelPaidActive =
          channel?.paidUntilEpoch && new Date(channel.paidUntilEpoch).getTime() > Date.now()
        setAlreadyPaid(!!s.hasTariff || !!channelPaidActive)
        setKnownBranch(s.branchPath || null)
      })
      .catch(() => {
        if (cancelled) return
        // Бэк недоступен — fallback на /channels.paidUntilEpoch
        const channelPaidActive =
          channel?.paidUntilEpoch && new Date(channel.paidUntilEpoch).getTime() > Date.now()
        if (channelPaidActive) setAlreadyPaid(true)
      })
    return () => {
      cancelled = true
    }
  }, [channelUUID, channel?.paidUntilEpoch])

  function handleSkipForward() {
    const target = knownBranch === 'ads' ? '/onboarding/creatives-editor' : '/onboarding/final'
    const nextStep = knownBranch === 'ads' ? 'creatives-editor' : 'final'
    bookmarkStep(nextStep)
    navigate(target, { replace: true })
  }

  function validateEmail(value: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
  }

  async function handlePay() {
    setSubmitError(null)
    if (!acceptedTerms) {
      // Не блокируем кнопку серым — по клику ведём юзера к чекбоксу: скроллим
      // и подсвечиваем, чтобы было понятно ЧТО мешает оплатить.
      legalRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      setLegalFlash(true)
      setTimeout(() => setLegalFlash(false), 1600)
      setSubmitError('Отметьте согласие с офертой ниже — и можно оплачивать.')
      return
    }
    if (!validateEmail(email)) {
      emailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      setEmailFlash(true)
      setTimeout(() => setEmailFlash(false), 1600)
      document.querySelector<HTMLInputElement>('.email-input')?.focus()
      setEmailError('Введите корректный email — он нужен для чека ЮКассы.')
      return
    }
    if (!channel) {
      setSubmitError('Канал не найден. Вернитесь и подключите канал.')
      return
    }
    setEmailError(null)
    setSubmitting(true)
    try {
      // Сохраняем email на финал-экране показываем «Чек отправлен на email …»
      try { sessionStorage.setItem('tgx-payment-email', email) } catch {}
      const res = await startTrialCheckout(channel.maxChannelID, email, PAYMENT_VARIANT)
      if (!res.success || !res.paymentUrl) {
        throw new Error(res.error || 'Сервис оплаты вернул пустой ответ')
      }
      // Намеренный click — фиксируем перед редиректом на ЮКассу. Сравнение с
      // payment_success даст конверсию «нажал на оплату» → «оплатил».
      track('tariff_pay_clicked', {
        variant: PAYMENT_VARIANT,
        message: `клик «Оплатить» · ${PAYMENT_VARIANT}`,
      })
      setPollingPaid(true)
      window.location.href = res.paymentUrl
    } catch (e) {
      trackError(e, 'start-trial-checkout')
      setSubmitError(e instanceof Error ? e.message : 'Не удалось создать платёж')
      setSubmitting(false)
    }
  }

  return (
    <OnboardingShell
      step="tariff"
      footer={
        <>
          <button className="foot-back" onClick={() => navigate(-1)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 18l-6-6 6-6" />
            </svg>
            Назад
          </button>
          <div className="foot-spacer" />
          <button className="foot-next" onClick={handlePay} disabled={submitting}>
            {submitting ? 'Создаём счёт…' : `Оплатить ${PRICE_LABEL}`}
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 6l6 6-6 6" />
            </svg>
          </button>
        </>
      }
    >
      <div className="tariff-wrap">
        <p className="stage-eyebrow">
          <span className="stage-eyebrow-pill">Тариф</span>
          Один план, всё включено
        </p>
        <h1 className="stage-title">
          Профи&nbsp;— <em>один тариф под&nbsp;всё</em>.
        </h1>
        <p className="stage-sub">
          Оплата&nbsp;— и&nbsp;вы&nbsp;готовы. Дальше соберём креативы и&nbsp;запустим первую кампанию&nbsp;Я.Директа. <b>Без триала и&nbsp;скрытых списаний.</b>
        </p>

        <div className="tariff-anchor">
          <div className="tariff-anchor-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="9" />
              <path d="M12 7v5l3 2" />
            </svg>
          </div>
          <div className="tariff-anchor-body">
            <div className="tariff-anchor-title">30&nbsp;часов работы специалистов&nbsp;— за&nbsp;10&nbsp;минут</div>
            <div className="tariff-anchor-sub">
              Аналитика канала, креативы под аудиторию, настройка таргета, маркировка, антиспам&nbsp;— всё в&nbsp;одном тарифе. Без агентства.
            </div>
          </div>
        </div>

        {alreadyPaid && (
          <div className="already-paid-banner">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 6L9 17l-5-5" />
            </svg>
            <div className="ap-text">
              <b>Тариф Профи уже активен</b> для этого канала. Можно перейти дальше или оплатить ещё раз для тестирования.
            </div>
            <button className="ap-skip" onClick={handleSkipForward}>
              Перейти дальше →
            </button>
          </div>
        )}

        {searchParams.get('payment') === 'cancelled' && (
          <div className="payment-cancelled-banner">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="9" />
              <path d="M12 8v4M12 16v.01" />
            </svg>
            <div className="pcb-text">
              <b>Оплата не завершена.</b> Ничего не списали, можно вернуться к оформлению в любой момент.
            </div>
            <button
              className="pcb-close"
              type="button"
              onClick={() => {
                const next = new URLSearchParams(searchParams)
                next.delete('payment')
                setSearchParams(next, { replace: true })
              }}
            >
              Понятно
            </button>
          </div>
        )}

        <div className="tariff-block">
          <div className="tariff-card pro">
            <div className="tariff-pro-pill">профи</div>
            <div className="tariff-pro-name">TeleGraphyx Профи</div>
            <div className="tariff-pro-price">
              <span className="tariff-pro-num">{PRICE_NUM}</span>
              <span className="tariff-pro-unit">{PRICE_UNIT}</span>
            </div>
            {PAYMENT_VARIANT === 'trial' && (
              <div className="tariff-pro-test-note">⚠️ Тестовый режим · {PRICE_LABEL} · {PRICE_PERIOD}</div>
            )}

            <ul className="tariff-feats">
              <Feat>Канал, группа и связанная инфраструктура</Feat>
              <Feat>Полная аналитика: охваты, ER, источники, посты</Feat>
              <Feat>
                Реклама в Я.Директе <b>без комиссии агента</b>
              </Feat>
              <Feat>
                Лендинги <code>tgryx.ru/slug</code> + супер-лендинги
              </Feat>
              <Feat>Умные кнопки и счётчики кликов</Feat>
              <Feat>Антиспам и авто-ответы в комментариях</Feat>
              <Feat>Поддержка по будням, 09–21 МСК</Feat>
            </ul>
          </div>

        </div>

        <div className="prepay-card">
          <div className="prepay-recap">
            <span className="prepay-check">✓</span> Канал подключён
            <span className="prepay-dot">·</span>
            <span className="prepay-check">✓</span> Я.Директ авторизован
            <span className="prepay-dot">·</span>
            <span className="prepay-check">✓</span> Реквизиты заполнены
          </div>
          <div className="prepay-title">
            Остался последний шаг — активировать тариф.
          </div>
          <div className="prepay-sub">
            Сразу после оплаты соберём креативы под вашу аудиторию и запустим кампании в&nbsp;Я.Директе.
          </div>
          <div className="prepay-anchors">
            <div className="prepay-anchor">
              <b>≈{PRICE_PER_DAY}&nbsp;₽/день</b>
              <span>меньше часа работы директолога</span>
            </div>
            <div className="prepay-anchor">
              <b>Отмена в&nbsp;один клик</b>
              <span>без звонков и&nbsp;удержаний</span>
            </div>
          </div>
        </div>

        <div className={`email-card ${emailFlash ? 'flash' : ''}`} ref={emailRef}>
          <div className="email-head">
            <div>
              <div className="email-badge">Нужно перед оплатой</div>
              <label className="email-label">Email для чека</label>
            </div>
            <div className={`email-state ${emailError ? 'error' : validateEmail(email) ? 'ok' : ''}`}>
              {emailError ? 'Проверьте email' : validateEmail(email) ? 'Готово к оплате' : 'Укажите email'}
            </div>
          </div>
          <input
            type="email"
            className={`email-input ${emailError ? 'error' : validateEmail(email) ? 'ok' : ''}`}
            placeholder="vasya@example.com"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value)
              if (emailError) setEmailError(null)
            }}
            disabled={submitting}
          />
          {emailError && <div className="email-error">{emailError}</div>}
          <div className="email-hint">
            ЮКасса пришлёт чек на этот email. Безопасная оплата · отмена в&nbsp;один&nbsp;клик · возврат в&nbsp;течение 14&nbsp;дней.
          </div>
        </div>

        <div className={`legal-block ${legalFlash ? 'flash' : ''}`} ref={legalRef}>
          <label className={`legal-row ${legalFlash && !acceptedTerms ? 'flash-row' : ''}`}>
            <input
              type="checkbox"
              checked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
              disabled={submitting}
            />
            <span>
              Я принимаю условия{' '}
              <a href="https://telegraphyx.ru/public-offer/" target="_blank" rel="noopener noreferrer">публичной оферты</a>,{' '}
              <a href="https://telegraphyx.ru/privacy-policy/" target="_blank" rel="noopener noreferrer">политики конфиденциальности</a>{' '}
              и даю{' '}
              <a href="https://telegraphyx.ru/personalsoglasio/" target="_blank" rel="noopener noreferrer">согласие на обработку персональных данных</a>.
            </span>
          </label>
          <label className="legal-row">
            <input
              type="checkbox"
              checked={acceptedMarketing}
              onChange={(e) => setAcceptedMarketing(e.target.checked)}
              disabled={submitting}
            />
            <span>
              Согласен получать <a href="https://telegraphyx.ru/reklamasoglasio/" target="_blank" rel="noopener noreferrer">рекламные рассылки</a> о новых функциях и предложениях (необязательно).
            </span>
          </label>
        </div>

        {submitError && (
          <div className="tariff-error">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="9" />
              <path d="M12 8v4M12 16v.01" />
            </svg>
            <div>
              <b>Не удалось создать платёж.</b>
              <div>{submitError}</div>
            </div>
          </div>
        )}
      </div>

      <style>{`
        .tariff-wrap {
          max-width: 880px;
        }
        .prepay-card {
          margin: 8px 0 18px;
          padding: 20px 22px;
          background: var(--tg-surface, #fff);
          border: 1.5px solid var(--tg-blue, #0080ff);
          border-radius: 16px;
          box-shadow: 0 8px 24px -12px rgba(0,128,255,0.35);
        }
        .prepay-recap {
          font-size: 12.5px;
          color: var(--tg-mute, #5b6678);
          margin-bottom: 10px;
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 5px;
        }
        .prepay-check { color: var(--tg-up, #00a860); font-weight: 800; }
        .prepay-dot { color: var(--tg-line-3, #d0d0d8); }
        .prepay-title {
          font-size: 17px;
          font-weight: 800;
          color: var(--tg-ink, #111);
          line-height: 1.3;
          margin-bottom: 6px;
        }
        .prepay-sub {
          font-size: 13.5px;
          color: var(--tg-ink-2, #333);
          line-height: 1.5;
          margin-bottom: 16px;
        }
        .prepay-anchors {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
        }
        .prepay-anchor {
          flex: 1;
          min-width: 160px;
          padding: 12px 14px;
          background: var(--tg-paper, #f7f8fb);
          border-radius: 10px;
          display: flex;
          flex-direction: column;
          gap: 2px;
        }
        .prepay-anchor b {
          font-size: 15px;
          font-weight: 800;
          color: var(--tg-ink, #111);
        }
        .prepay-anchor span {
          font-size: 12px;
          color: var(--tg-mute, #5b6678);
        }
        .tariff-anchor {
          margin: 18px 0 24px;
          padding: 16px 20px;
          background: linear-gradient(135deg, rgba(0,128,255,0.06), rgba(0,128,255,0.02));
          border: 1px solid rgba(0,128,255,0.15);
          border-radius: 14px;
          display: flex;
          gap: 14px;
          align-items: flex-start;
        }
        .tariff-anchor-icon {
          flex-shrink: 0;
          width: 36px;
          height: 36px;
          border-radius: 10px;
          background: var(--tg-blue, #0080ff);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .tariff-anchor-icon svg { width: 20px; height: 20px; }
        .tariff-anchor-body { flex: 1; min-width: 0; }
        .tariff-anchor-title {
          font-weight: 700;
          font-size: 15px;
          color: var(--tg-ink, #111);
          line-height: 1.3;
          margin-bottom: 4px;
        }
        .tariff-anchor-sub {
          font-size: 13px;
          color: var(--tg-mute, #5b6678);
          line-height: 1.5;
        }
        .tariff-block {
          display: grid;
          grid-template-columns: 1.4fr 1fr;
          gap: 16px;
          margin-bottom: 24px;
        }
        @media (max-width: 800px) {
          .tariff-block { grid-template-columns: 1fr; }
        }
        .tariff-card {
          background: var(--tg-surface, #fff);
          border-radius: 16px;
          padding: 24px;
        }
        .tariff-card.pro {
          border: 0;
          background: linear-gradient(135deg, #1a1d24 0%, #2a2f3a 100%);
          color: white;
        }
        .tariff-pro-pill {
          display: inline-block;
          padding: 4px 10px;
          font-size: 10px;
          font-weight: 800;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          background: rgba(255, 213, 80, 0.16);
          color: #FFD550;
          border-radius: 999px;
          margin-bottom: 12px;
        }
        .tariff-pro-name {
          font-weight: 800;
          font-size: 22px;
          letter-spacing: -0.02em;
          margin-bottom: 8px;
        }
        .tariff-pro-price {
          display: flex;
          align-items: baseline;
          gap: 6px;
          font-variant-numeric: tabular-nums;
          margin-bottom: 16px;
        }
        .tariff-pro-num {
          font-weight: 800;
          font-size: 36px;
          letter-spacing: -0.025em;
        }
        .tariff-pro-unit {
          font-size: 14px;
          color: rgba(255,255,255,0.5);
          font-weight: 600;
        }
        .tariff-pro-test-note {
          font-size: 11px;
          color: #FFD550;
          font-weight: 700;
          letter-spacing: 0.04em;
          padding: 4px 10px;
          background: rgba(255, 213, 80, 0.12);
          border-radius: 6px;
          display: inline-block;
          margin-bottom: 10px;
        }
        .tariff-feats {
          list-style: none;
          margin: 0 0 16px;
          padding: 0;
          display: flex;
          flex-direction: column;
          gap: 8px;
          font-size: 13px;
        }
        .tariff-feats li {
          display: flex;
          align-items: flex-start;
          gap: 8px;
          line-height: 1.4;
        }
        .tariff-feats li svg {
          width: 14px;
          height: 14px;
          color: #FFD550;
          flex-shrink: 0;
          margin-top: 2px;
        }
        .tariff-feats li b { font-weight: 700; }
        .tariff-feats li code {
          font-family: ui-monospace, SFMono-Regular, monospace;
          font-size: 12px;
          background: rgba(255,255,255,0.08);
          color: #FFD550;
          padding: 1px 6px;
          border-radius: 4px;
        }

        .email-card {
          margin: 2px 0 0;
          max-width: 760px;
          background: linear-gradient(135deg, rgba(0,128,255,0.07), rgba(0,128,255,0.02));
          border: 1px solid rgba(0,128,255,0.20);
          border-radius: 16px;
          padding: 24px;
          display: flex;
          flex-direction: column;
          gap: 10px;
          transition: border-color 200ms, box-shadow 200ms;
        }
        .email-card.flash {
          border-color: var(--tg-blue, #0080ff);
          box-shadow: 0 0 0 3px rgba(0,128,255,0.18);
          animation: email-pulse 0.5s ease-in-out 2;
        }
        @keyframes email-pulse {
          0%, 100% { box-shadow: 0 0 0 3px rgba(0,128,255,0.18); }
          50% { box-shadow: 0 0 0 6px rgba(0,128,255,0.28); }
        }
        .email-head {
          display: flex;
          gap: 12px;
          justify-content: space-between;
          align-items: flex-start;
        }
        .email-badge {
          display: inline-flex;
          align-items: center;
          padding: 4px 10px;
          margin-bottom: 10px;
          border-radius: 999px;
          background: var(--tg-blue-bg, #ebf2ff);
          color: var(--tg-blue-press, #0052cc);
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.06em;
          text-transform: uppercase;
        }
        .email-label {
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: var(--tg-mute, #8f9eb0);
        }
        .email-state {
          flex-shrink: 0;
          padding: 6px 10px;
          border-radius: 999px;
          background: rgba(0,128,255,0.08);
          color: var(--tg-blue-press, #0052cc);
          font-size: 11.5px;
          font-weight: 700;
          white-space: nowrap;
        }
        .email-state.ok {
          background: rgba(0,168,96,0.10);
          color: var(--tg-up, #00a860);
        }
        .email-state.error {
          background: rgba(255, 64, 0, 0.10);
          color: #c33;
        }
        .email-input {
          padding: 12px 14px;
          border: 1px solid var(--tg-line-2, #f0f0f4);
          border-radius: 10px;
          font-family: inherit;
          font-size: 14px;
          color: var(--tg-ink, #111);
          background: var(--tg-surface, #fff);
        }
        .email-input:focus {
          outline: 0;
          border-color: var(--tg-blue, #0080ff);
        }
        .email-input.ok {
          border-color: rgba(0, 168, 96, 0.35);
          background: rgba(0, 168, 96, 0.03);
        }
        .email-input.error {
          border-color: #c33;
          background: rgba(255, 64, 0, 0.03);
        }
        .email-error {
          font-size: 12px;
          color: #c33;
        }
        .email-hint {
          font-size: 12px;
          color: var(--tg-mute, #8f9eb0);
          line-height: 1.5;
          padding-top: 2px;
        }

        .tariff-error {
          margin-top: 16px;
          padding: 14px 18px;
          background: rgba(255, 64, 0, 0.08);
          border: 1px solid rgba(255, 64, 0, 0.25);
          border-radius: 12px;
          display: flex;
          gap: 12px;
          font-size: 13px;
          color: var(--tg-ink-2, #333);
          max-width: 760px;
        }
        .tariff-error svg {
          width: 20px;
          height: 20px;
          color: #c33;
          flex-shrink: 0;
        }
        .tariff-error b { color: var(--tg-ink, #111); font-weight: 700; }

        .legal-block {
          margin-top: 12px;
          padding: 14px 18px;
          background: var(--tg-surface, #fff);
          border: 1px solid var(--tg-line-2, #f0f0f4);
          border-radius: 12px;
          display: flex;
          flex-direction: column;
          gap: 10px;
          max-width: 760px;
          transition: border-color 200ms, box-shadow 200ms;
        }
        .legal-block.flash {
          border-color: var(--tg-blue, #0080ff);
          box-shadow: 0 0 0 3px rgba(0,128,255,0.18);
          animation: legal-pulse 0.5s ease-in-out 2;
        }
        @keyframes legal-pulse {
          0%, 100% { box-shadow: 0 0 0 3px rgba(0,128,255,0.18); }
          50% { box-shadow: 0 0 0 6px rgba(0,128,255,0.30); }
        }
        .legal-row.flash-row input[type="checkbox"] {
          outline: 2px solid var(--tg-blue, #0080ff);
          outline-offset: 2px;
          border-radius: 3px;
        }
        .legal-row {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          font-size: 12.5px;
          line-height: 1.5;
          color: var(--tg-ink-2, #333);
          cursor: pointer;
        }
        .legal-row input[type="checkbox"] {
          margin: 2px 0 0;
          flex-shrink: 0;
          width: 16px; height: 16px;
          accent-color: var(--tg-blue, #0080ff);
          cursor: pointer;
        }
        .legal-row a {
          color: var(--tg-blue, #0080ff);
          text-decoration: none;
        }
        .legal-row a:hover { text-decoration: underline; }

        .already-paid-banner {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 14px 18px;
          background: rgba(0, 168, 96, 0.08);
          border: 1px solid rgba(0, 168, 96, 0.25);
          border-radius: 12px;
          margin-bottom: 20px;
          max-width: 880px;
        }
        .already-paid-banner svg {
          width: 20px;
          height: 20px;
          color: var(--tg-up, #00a860);
          flex-shrink: 0;
        }
        .ap-text {
          flex: 1;
          font-size: 13px;
          color: var(--tg-ink-2, #333);
          line-height: 1.5;
        }
        .ap-text b { color: var(--tg-ink, #111); font-weight: 700; }
        .ap-skip {
          padding: 8px 14px;
          background: var(--tg-up, #00a860);
          color: white;
          border: 0;
          border-radius: 8px;
          font-family: inherit;
          font-weight: 700;
          font-size: 12px;
          cursor: pointer;
          flex-shrink: 0;
        }
        .ap-skip:hover { background: #00805a; }
        .payment-cancelled-banner {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 14px 18px;
          background: rgba(255, 64, 0, 0.08);
          border: 1px solid rgba(255, 64, 0, 0.22);
          border-radius: 12px;
          margin-bottom: 20px;
          max-width: 880px;
        }
        .payment-cancelled-banner svg {
          width: 20px;
          height: 20px;
          color: #c33;
          flex-shrink: 0;
        }
        .pcb-text {
          flex: 1;
          font-size: 13px;
          color: var(--tg-ink-2, #333);
          line-height: 1.5;
        }
        .pcb-text b { color: var(--tg-ink, #111); font-weight: 700; }
        .pcb-close {
          padding: 8px 14px;
          background: white;
          color: var(--tg-ink, #111);
          border: 1px solid rgba(255, 64, 0, 0.18);
          border-radius: 8px;
          font-family: inherit;
          font-weight: 700;
          font-size: 12px;
          cursor: pointer;
          flex-shrink: 0;
        }
        .pcb-close:hover { background: #fff5f5; }
        @media (max-width: 720px) {
          .payment-cancelled-banner {
            align-items: flex-start;
            flex-wrap: wrap;
          }
          .email-card {
            padding: 18px 16px;
          }
          .email-head {
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
          }
          .email-state {
            white-space: normal;
          }
        }
      `}</style>
    </OnboardingShell>
  )
}

function Feat({ children }: { children: React.ReactNode }) {
  return (
    <li>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M5 12l5 5L20 7" />
      </svg>
      <span>{children}</span>
    </li>
  )
}
